import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  buildAudienceApi,
  saveSegmentApi,
  updateSegmentApi,
  getAudienceFiltersApi,
  exportAudienceMatchApi,
} from '../api/audience.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import PageHeader from '../components/PageHeader';
import {
  defaultAudienceFilters,
  defaultExclusions,
  toBuildPayload,
  hydrateFiltersFromDefinition,
  hydrateExclusionsFromDefinition,
  parseFilterDefinition,
  buildDynamicOptions,
  BEHAVIOR_OPTIONS,
  CIRCLE_OPTIONS,
  VERIFICATION_OPTIONS,
  PLAN_OPTIONS,
  SEGMENT_TYPE_OPTIONS,
} from '../utils/audienceBuilderFilters';

const planClassMap = {
  freemium: 'ab-badge-freemium',
  single: 'ab-badge-guardian',
  group: 'ab-badge-guardian',
  sponsored: 'ab-badge-guardian',
  pilot: 'ab-badge-guardian',
  promo: 'ab-badge-guardian',
  paid: 'ab-badge-guardian',
  guardian: 'ab-badge-guardian',
  premium: 'ab-badge-guardian',
};

const exclusionsList = [
  { key: 'e1', label: 'Exclude opted-out users' },
  { key: 'e2', label: 'Exclude messages sent in last 72h' },
  { key: 'e3', label: 'Exclude suspended accounts' },
];

export default function AudienceBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const editState = location.state || {};

  const [filters, setFilters] = useState(defaultAudienceFilters());
  const [excl, setExcl] = useState(defaultExclusions());
  const [logic, setLogic] = useState('');
  const [matchedTotal, setMatchedTotal] = useState(0);
  const [primaryCircles, setPrimaryCircles] = useState(0);
  const [sampleRecords, setSampleRecords] = useState([]);
  const [reachData, setReachData] = useState([]);
  const [lastFilterDef, setLastFilterDef] = useState(null);
  const [building, setBuilding] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [toast, setToast] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveForm, setSaveForm] = useState({
    segmentName: '',
    description: '',
    segmentType: 'dynamic',
  });
  const [editSegmentId, setEditSegmentId] = useState(editState.segmentId || null);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  useEffect(() => {
    async function loadOptions() {
      const result = await apiHandler(() => getAudienceFiltersApi());
      if (result.success) setFilterOptions(unwrapData(result));
    }
    loadOptions();
  }, []);

  useEffect(() => {
    const def = parseFilterDefinition(editState.filterDefinition);
    if (!Object.keys(def).length && !editState.segmentId) return;

    setFilters(hydrateFiltersFromDefinition(def));
    setExcl(hydrateExclusionsFromDefinition(def));
    if (editState.segmentName) {
      setSaveForm((p) => ({
        ...p,
        segmentName: editState.segmentName,
        description: editState.description || '',
        segmentType: editState.segmentType || 'dynamic',
      }));
    }
    if (editState.segmentId) {
      setEditSegmentId(editState.segmentId);
      runBuild(def);
    }
  }, []);

  async function runBuild(filterOverride) {
    setBuilding(true);
    // Guard: if called directly from a button click, the event object is passed as
    // filterOverride. Reject it — only accept a plain object (a real filterDefinition).
    const isValidOverride =
      filterOverride &&
      typeof filterOverride === 'object' &&
      !(filterOverride instanceof Event) &&
      !filterOverride.nativeEvent;
    const payload = isValidOverride ? filterOverride : toBuildPayload(filters, excl, logic);
    const result = await apiHandler(() => buildAudienceApi(payload));
    if (result.success) {
      const data = unwrapData(result);
      const total = data?.totalMatched ?? data?.totalUsers ?? 0;
      setMatchedTotal(total);
      setLogic(data?.logicExpression || logic);
      setPrimaryCircles(data?.primaryCirclesCount ?? 0);
      setLastFilterDef(data?.filterDefinition || payload);

      const samples = (data?.sampleUsers || data?.users || []).slice(0, 8).map((u) => ({
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—',
        state: u.state || '—',
        plan: u.planType || '—',
        planClass: planClassMap[(u.planType || '').toLowerCase()] || 'ab-badge-freemium',
      }));
      setSampleRecords(samples);

      const reach = data?.reachByChannel || {};
      setReachData(
        ['push', 'sms', 'email'].map((ch, i) => {
          const key = ch;
          const entry = reach[key] || {};
          return {
            channel: ch.charAt(0).toUpperCase() + ch.slice(1),
            color: ['#6366F1', '#0EA5E9', '#10B981'][i],
            reach: String(entry.count ?? 0),
            rate: entry.rate ?? 0,
          };
        })
      );
      if (total === 0) showToast('No users matched these rules', 'info');
    } else {
      showToast(result.message || 'Build failed', 'error');
    }
    setBuilding(false);
  }

  async function handleSaveSegment() {
    if (!saveForm.segmentName.trim()) {
      showToast('Segment name is required', 'error');
      return;
    }
    const filterDefinition = lastFilterDef || toBuildPayload(filters, excl, logic);
    const body = {
      segmentName: saveForm.segmentName.trim(),
      description: saveForm.description.trim(),
      filterDefinition,
      segmentType: saveForm.segmentType,
      estimatedUserCount: matchedTotal,
    };

    const result = editSegmentId
      ? await apiHandler(() => updateSegmentApi(editSegmentId, body))
      : await apiHandler(() => saveSegmentApi(body));

    if (result.success) {
      showToast(editSegmentId ? 'Segment updated' : 'Segment saved', 'success');
      setShowSaveModal(false);
      navigate('/saved-segments');
    } else {
      showToast(result.message || 'Save failed', 'error');
    }
  }

  async function handleExport() {
    const filterDefinition = lastFilterDef || toBuildPayload(filters, excl, logic);
    const result = await apiHandler(() => exportAudienceMatchApi({ filterDefinition }));
    if (result.success && result.data) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audience-match.csv';
      a.click();
      URL.revokeObjectURL(url);
      showToast('Export downloaded', 'success');
    } else {
      showToast(result.message || 'Export failed', 'error');
    }
  }

  // Build dynamic options from API response, falling back to static defaults
  const planOptions = buildDynamicOptions(
    filterOptions?.plans,
    PLAN_OPTIONS,
    null
  );

  const productOptions = filterOptions?.products?.length
    ? [
        { value: '', label: 'Any product' },
        ...filterOptions.products.map((p) => ({
          value: typeof p === 'string' ? p : p.name,
          label: typeof p === 'string' ? p : p.name,
        })),
      ]
    : [
        { value: '', label: 'Any product' },
        { value: 'Urgent Assist', label: 'Urgent Assist' },
        { value: 'Trusted Circle', label: 'Trusted Circle' },
        { value: 'Roadside Assistance', label: 'Roadside Assistance' },
        { value: 'Urgent Medical Assist', label: 'Urgent Medical Assist' },
      ];

  // behaviors and circleStatuses come as { value, label } objects from the API
  const behaviorOptions = filterOptions?.behaviors?.length
    ? buildDynamicOptions(filterOptions.behaviors, BEHAVIOR_OPTIONS, 'label')
    : BEHAVIOR_OPTIONS;

  const circleOptions = filterOptions?.circleStatuses?.length
    ? buildDynamicOptions(filterOptions.circleStatuses, CIRCLE_OPTIONS, 'label')
    : CIRCLE_OPTIONS;

  const stateHint = filterOptions?.states?.length
    ? filterOptions.states.join(', ').toUpperCase()
    : 'WI, TX';

  return (
    <>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 70,
            right: 20,
            zIndex: 9999,
            background: toast.type === 'success' ? '#F0FDF4' : '#EFF6FF',
            border: `1px solid ${toast.type === 'success' ? '#b7e5bb' : '#BFDBFE'}`,
            color: toast.type === 'success' ? '#15803D' : '#1D4ED8',
            padding: '10px 18px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,.08)',
          }}
        >
          {toast.msg}
        </div>
      )}

      <PageHeader title="Audience Builder" icon="fa-bullseye" trail={[{ label: 'Audience Builder' }]} />

      <div className="ab-grid fade-up">
        <div className="ab-card">
          <div className="ab-card-header">
            <span className="ab-card-title"><i className="fas fa-sliders" /> Filters</span>
          </div>
          <div className="ab-card-body">
            <div className="ab-field-group">
              <label className="ab-field-label">Geography</label>
              <div className="ab-input-wrap">
                <i className="fas fa-map-marker-alt ab-input-icon" />
                <input
                  type="text"
                  className="ab-input"
                  placeholder={`e.g. ${stateHint}`}
                  value={filters.geo}
                  onChange={(e) => setFilters((p) => ({ ...p, geo: e.target.value }))}
                />
              </div>
            </div>

            {[
              { key: 'plan', label: 'Plan', icon: 'fa-tag', options: planOptions },
              { key: 'product', label: 'Product', icon: 'fa-box', options: productOptions },
              { key: 'circle', label: 'Trusted Circle', icon: 'fa-circle-nodes', options: circleOptions },
              { key: 'behavior', label: 'Behavior', icon: 'fa-chart-line', options: behaviorOptions },
              { key: 'verify', label: 'Verification', icon: 'fa-shield-halved', options: VERIFICATION_OPTIONS },
            ].map(({ key, label, icon, options }) => (
              <div key={key} className="ab-field-group">
                <label className="ab-field-label">{label}</label>
                <div className="ab-select-wrap">
                  <i className={`fas ${icon} ab-input-icon`} />
                  <select
                    className="ab-select"
                    value={filters[key]}
                    onChange={(e) => setFilters((p) => ({ ...p, [key]: e.target.value }))}
                  >
                    {options.map((o) => (
                      <option key={o.value ?? 'any'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down ab-select-arrow" />
                </div>
              </div>
            ))}

            <div className="ab-filter-actions">
              <button
                type="button"
                className="action-btn btn-green ab-btn-full"
                onClick={() => runBuild()}
                disabled={building}
              >
                <i className="fas fa-plus" /> {building ? 'Building…' : 'Build Audience'}
              </button>
              <button
                type="button"
                className="ab-btn-clear"
                onClick={() => {
                  setFilters(defaultAudienceFilters());
                  setExcl(defaultExclusions());
                  setLogic('');
                  setMatchedTotal(0);
                  setSampleRecords([]);
                  setReachData([]);
                }}
              >
                <i className="fas fa-rotate-left" /> Clear
              </button>
            </div>
          </div>
        </div>

        <div className="ab-card">
          <div className="ab-card-header">
            <span className="ab-card-title"><i className="fas fa-code-branch" /> Rule logic</span>
          </div>
          <div className="ab-card-body">
            <div className="ab-section-label">Current logic</div>
            <div className="ab-field-group">
              <label className="ab-field-label">Expression</label>
              <textarea
                className="ab-textarea"
                rows={4}
                value={logic}
                onChange={(e) => setLogic(e.target.value)}
                placeholder="Run Build Audience to generate expression, or type custom rules"
              />
            </div>

            <div className="ab-section-label" style={{ marginTop: 8 }}>Exclusions</div>
            <div className="ab-exclusions">
              {exclusionsList.map(({ key, label }) => (
                <label
                  key={key}
                  className="ab-check-row"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExcl((p) => ({ ...p, [key]: !p[key] }))}
                >
                  <span className={`ab-checkbox${excl[key] ? ' checked' : ''}`}>
                    {excl[key] && <i className="fas fa-check ab-check-icon" />}
                  </span>
                  <span className="ab-check-text">{label}</span>
                </label>
              ))}
            </div>

            <div className="ab-section-label" style={{ marginTop: 16 }}>Reach preview</div>
            <div className="ab-reach-table-wrap">
              <table className="ab-reach-table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Reach</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {reachData.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ color: '#94A3B8', fontSize: 12 }}>
                        Build audience to preview reach
                      </td>
                    </tr>
                  ) : (
                    reachData.map((r) => (
                      <tr key={r.channel}>
                        <td>
                          <span className="ab-channel-dot" style={{ background: r.color }} />
                          {r.channel}
                        </td>
                        <td className="ab-reach-num">{r.reach}</td>
                        <td>
                          <div className="ab-rate-wrap">
                            <span className="ab-rate-text">{r.rate}%</span>
                            <div className="ab-rate-bar-bg">
                              <div
                                className="ab-rate-bar"
                                style={{ width: `${r.rate}%`, background: r.color }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="ab-card">
          <div className="ab-card-header">
            <span className="ab-card-title"><i className="fas fa-users-viewfinder" /> Matched audience</span>
          </div>
          <div className="ab-card-body">
            <div className="ab-stat-row">
              <div className="ab-stat-bubble ab-stat-green">
                <div className="ab-stat-dot" style={{ background: '#2E7D32' }} />
                <div>
                  <div className="ab-stat-sublabel">Matched users</div>
                  <div className="ab-stat-big">{matchedTotal.toLocaleString()}</div>
                </div>
              </div>
              <div className="ab-stat-bubble ab-stat-blue">
                <div className="ab-stat-dot" style={{ background: '#074f78' }} />
                <div>
                  <div className="ab-stat-sublabel">Primary circles</div>
                  <div className="ab-stat-big">{primaryCircles.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div className="ab-section-label" style={{ marginTop: 20 }}>Sample records</div>
            <div className="ab-sample-table-wrap">
              <table className="ab-sample-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>State</th>
                    <th>Plan</th>
                  </tr>
                </thead>
                <tbody>
                  {sampleRecords.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ color: '#94A3B8', fontSize: 12 }}>
                        No samples yet — run Build Audience
                      </td>
                    </tr>
                  ) : (
                    sampleRecords.map((r, i) => (
                      <tr key={i}>
                        <td>{r.name}</td>
                        <td>
                          <span className={`ab-state-badge${r.state === 'TX' ? ' ab-state-tx' : ''}`}>
                            {r.state}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${r.planClass}`}>{r.plan}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="ab-action-group">
              <button
                type="button"
                className="action-btn btn-blue ab-action-half"
                onClick={() => setShowSaveModal(true)}
                disabled={building}
              >
                <i className="fas fa-bookmark" /> {editSegmentId ? 'Update Segment' : 'Save Segment'}
              </button>
              <button
                type="button"
                className="action-btn btn-green ab-action-half"
                onClick={() =>
                  navigate('/message-composer', {
                    state: { filterDefinition: lastFilterDef || toBuildPayload(filters, excl, logic) },
                  })
                }
              >
                <i className="fas fa-paper-plane" /> Send Message
              </button>
            </div>
            <button
              type="button"
              className="action-btn btn-orange ab-btn-full ab-export-btn"
              onClick={handleExport}
              disabled={!lastFilterDef && matchedTotal === 0}
            >
              <i className="fas fa-file-export" /> Export Match List
            </button>
          </div>
        </div>
      </div>

      {showSaveModal && (
        <div
          className="ss-modal-overlay show"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowSaveModal(false);
          }}
        >
          <div className="ss-modal">
            <div className="ss-modal-header">
              <div className="ss-modal-icon" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                <i className="fas fa-bookmark" />
              </div>
              <div className="ss-modal-title">{editSegmentId ? 'Update Segment' : 'Save Segment'}</div>
              <button type="button" className="ss-modal-close" onClick={() => setShowSaveModal(false)}>
                <i className="fas fa-xmark" />
              </button>
            </div>
            <div className="ss-modal-body">
              <div className="ss-form-group">
                <label className="ss-form-label">Segment name</label>
                <input
                  type="text"
                  className="ss-form-input"
                  value={saveForm.segmentName}
                  onChange={(e) => setSaveForm((p) => ({ ...p, segmentName: e.target.value }))}
                  placeholder="e.g. Inactive WI users"
                />
              </div>
              <div className="ss-form-group">
                <label className="ss-form-label">Description</label>
                <textarea
                  className="ss-form-input ss-form-textarea"
                  rows={2}
                  value={saveForm.description}
                  onChange={(e) => setSaveForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="ss-form-group">
                <label className="ss-form-label">Type</label>
                <select
                  className="ss-form-select"
                  value={saveForm.segmentType}
                  onChange={(e) => setSaveForm((p) => ({ ...p, segmentType: e.target.value }))}
                >
                  {SEGMENT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <p style={{ fontSize: 12, color: '#64748B' }}>
                Estimated reach: <strong>{matchedTotal.toLocaleString()}</strong> users
              </p>
            </div>
            <div className="ss-modal-footer">
              <button
                type="button"
                className="action-btn"
                style={{ background: '#F1F5F9', color: '#64748B' }}
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button type="button" className="action-btn btn-blue" onClick={handleSaveSegment}>
                <i className="fas fa-check" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
